/**
 * Fetch-based SSE transport — SPA architectural convergence plan §6.4.
 *
 * Replaces the native `EventSource` path used in the legacy
 * `zenoh-sse.ts`. The legacy transport depended on the browser
 * `EventSource`, which:
 *   - cannot set the `Authorization` header (Bearer token must go
 *     through Envoy's `ext_authz` filter);
 *   - has no `AbortController` integration for epoch/sequence-gap
 *     recovery;
 *   - silently buffers events in the browser, defeating bounded
 *     queue + drop policies (plan §SSE loss/backpressure).
 *
 * The fetch-SSE transport uses `fetch()` with `ReadableStream` to
 * stream the SSE bytes incrementally, parses them line-by-line, and
 * routes them to the per-category reducers. Reconnect, token
 * refresh, and epoch/sequence gap recovery are explicit and bounded.
 *
 * Wire shape:
 *   - URL: `${cfg.apiBaseUrl}/live/v1/sites/${site}/**` (one
 *     connection per active site, broad pattern covering all v1
 *     categories the SPA subscribes to).
 *   - Auth: Bearer header on every connection.
 *   - Format: standard SSE (`: ping\n\n` heartbeats from the
 *     internal Zenoh plugin; otherwise `data: {json}\n\n` where the
 *     JSON is the stock Zenoh REST/SSE plugin's per-sample envelope
 *     `{key, value, encoding, timestamp}` — verified against a real
 *     `eclipse/zenoh` REST-plugin instance, NOT the bare `V1LiveEvent`
 *     shape. `value` is the `{envelope, payload}` body each v1
 *     publisher PUTs (matching `GET` snapshot responses exactly).
 *     `deriveV1LiveEvent()` below reconstructs the `V1LiveEvent`
 *     discriminant (`kind`/`category`) from the sample's `key`, since
 *     the wire body carries neither.
 */
import { getCurrentToken } from '../auth/token-store';
import { getRuntimeConfig } from '../config/runtime-config';
import { nextReconnectDelay } from './reconnect-backoff';
import { isValidEnvelope } from './v1-envelope';
import type { CategoryManifest, SiteId, Tombstone, V1Envelope, V1LiveEvent } from './v1';

// ----------------------------------------------------------------------------
// Wire-format reconstruction — the Zenoh REST/SSE plugin delivers
// `{key, value, encoding, timestamp}` samples, not `V1LiveEvent`
// directly. `key` determines the category/kind; `value` carries the
// `{envelope, payload}` body.
// ----------------------------------------------------------------------------

interface ZenohSample {
  key?: string;
  value?: unknown;
  encoding?: string;
  timestamp?: unknown;
}

// 'trajectories' is intentionally included here even though it has no v1
// CategoryName/manifest/tombstone contract (plan comment in useLiveData.ts)
// — without it, `deriveV1LiveEvent` silently drops every
// rmf/v1/sites/{site}/trajectories/{map}/state sample before it ever
// reaches a listener, which is why trajectories never rendered despite the
// backend publishing correctly (confirmed live: SSE samples arrived at the
// browser but this classification gate discarded them pre-`onEvent`).
type LiveCategory = 'fleets' | 'doors' | 'lifts' | 'robots' | 'tasks' | 'trajectories';
const LIVE_CATEGORIES: ReadonlySet<string> = new Set([
  'fleets', 'doors', 'lifts', 'robots', 'tasks', 'trajectories',
]);

function parseSampleValue(value: unknown): { envelope?: V1Envelope; payload?: unknown } | null {
  const parsed = typeof value === 'string' ? safeJsonParse(value) : value;
  if (!parsed || typeof parsed !== 'object') return null;
  return parsed as { envelope?: V1Envelope; payload?: unknown };
}

function safeJsonParse(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Reconstruct a `V1LiveEvent` from a raw SSE `data:` line. Returns
 * `null` for anything that isn't a live-relevant v1 sample (e.g. the
 * `_worker/heartbeat` liveness key, or the `building_map` key itself
 * — map bytes are never part of the broad live stream per the
 * consistency protocol; only `building_map_revisions/*` notifications
 * are).
 */
export function deriveV1LiveEvent(raw: string): V1LiveEvent | null {
  const sample = safeJsonParse(raw) as ZenohSample | null;
  if (!sample || typeof sample.key !== 'string') return null;
  const body = parseSampleValue(sample.value);
  if (!body || !isValidEnvelope(body.envelope) || body.payload === undefined) return null;
  const envelope = body.envelope;
  const payload = body.payload;

  // Key shape: rmf/v1/sites/{site}/{rest...} — segment 4 (0-indexed)
  // onward identifies the category/special topic.
  const segments = sample.key.split('/');
  const siteIdx = segments.indexOf('sites');
  const rest = siteIdx >= 0 ? segments.slice(siteIdx + 2) : [];
  const head = rest[0];
  const tail = rest[rest.length - 1];

  if (head === 'manifests') {
    return { kind: 'manifest', envelope, payload: payload as CategoryManifest };
  }
  if (head === 'tombstones') {
    return { kind: 'tombstone', envelope, payload: payload as Tombstone };
  }
  // `rmf/v1/sites/{site}/emergency/fire/state` — published by the
  // api-server's FireModeService, not the worker. Classified here
  // rather than added to LIVE_CATEGORIES because it is one state per
  // site with no entity id, so it has no reducer to feed; the category
  // path would drive it into `byId` under an empty key.
  if (head === 'emergency' && rest[1] === 'fire' && tail === 'state') {
    return {
      kind: 'fire_alarm',
      envelope,
      payload: payload as { trigger?: boolean; unix_millis_time?: number; siteId?: string },
    };
  }
  if (head === 'building_map_revisions') {
    return {
      kind: 'building_map_revision',
      envelope,
      payload: payload as { site: SiteId; level: string; revision: string },
    };
  }
  // `rmf/v1/sites/{site}/tasks/{task}/log` shares the same `head`
  // ('tasks') as `.../tasks/{task}/state` but is a completely
  // different payload shape (`{task_id, at_ms, level, text, seq}`,
  // no `id` field at all) published by the worker's task-history
  // projection (`rmf_bridge.py::_on_task_log_update`), not the v1
  // `CategoryName.TASKS` state category. Every other category's key
  // ends in `/state` too (`doors/{id}/state`, `robots/{id}/state`,
  // ...), so gating on the trailing segment here is what actually
  // distinguishes them — `head` alone can't. Misclassifying a log
  // entry as a task-state event fed it into the tasks reducer's
  // `byId` map (under key `''`, `idOf`'s defensive fallback for a
  // payload with no `.id`), and the next flush's
  // `adaptTaskSummary(entry)` crashed on `entry.id.task` reading
  // `undefined.task` — confirmed live: this took down the flush
  // loop's `for...of` before it reached `dirty.clear()`, which is
  // why unrelated categories (robots) stopped updating too and only
  // a full reload (a fresh reducer registry) cleared the poisoned
  // entry, until the next log entry re-triggered it. (The log key is
  // classified further down, as a `task_log` of its own.)
  if (head && LIVE_CATEGORIES.has(head) && tail === 'state') {
    return { kind: 'event', category: head as LiveCategory, envelope, payload };
  }
  // `rmf/v1/sites/{site}/task_distance/{task_id}` (worker 0.4.33): a
  // task's distance and cleaning-time totals. Its own kind and NOT a
  // LIVE_CATEGORIES entry: its tail is the task id, not 'state', and a
  // category event would feed the `tasks` reducer -- the
  // `tasks/{id}/log` crash above, again. Exactly two segments, so
  // nothing deeper under the head can pass for one. The payload is
  // validated by whoever consumes it (`adaptTaskDistance`), not here.
  //
  // Its sibling `robot_odometer/{robot}` is deliberately left
  // unclassified: no screen reads a robot's odometer live -- Metrics
  // reads the robot totals the api-server keeps -- so there is nothing
  // to hand it to.
  if (head === 'task_distance' && rest.length === 2 && rest[1]) {
    return { kind: 'task_distance', envelope, taskId: rest[1], payload };
  }
  // `rmf/v1/sites/{site}/tasks/{task_id}/log` -- one task log line
  // (`rmf_bridge.py::_on_task_log_update`): the crash above when it was
  // taken for a task state. Its own kind, so it can never be one again:
  // exactly three segments, the middle one the task id. Only the open
  // task drawer's Timeline listens (`useTaskLog`), and it checks the
  // task id and the payload itself.
  if (head === 'tasks' && rest.length === 3 && rest[1] && tail === 'log') {
    return { kind: 'task_log', envelope, taskId: rest[1], payload };
  }
  // 'building_map', '_worker', 'robot_odometer', or anything else
  // unrecognised — not part of the broad live stream's consumed surface
  // today.
  return null;
}

export type FetchSseState =
  | 'idle'
  | 'connecting'
  | 'open'
  | 'closed'
  | 'stale'
  | 'recovering';

export interface FetchSseListener {
  (event: V1LiveEvent): void;
}

/**
 * A transport state change, and WHICH site's stream it happened on.
 *
 * The site is the SECOND argument, appended, so every existing
 * one-argument listener (`useLiveStatus` passes `(s) => ...`) keeps
 * receiving exactly what it did. Putting it first would have handed
 * those callers a site id where they expect a state.
 */
export type FetchSseStateListener = (state: FetchSseState, site: SiteId) => void;

/**
 * One site's stream, read without subscribing (twin slice 1, plan W3
 * step 4) -- what the feed-health clamp is derived from.
 *
 * Both times are this device's `Date.now()`, stamped on receipt:
 * `lastEventAt` on a data event the classifier accepted, `lastByteAt`
 * on ANY bytes, pings included. Neither is a worker stamp. Both carry
 * over from earlier attempts, so neither says whether the stream open
 * NOW has delivered anything: `dataSinceOpen` does.
 */
export interface FetchSseSiteSnapshot {
  readonly site: SiteId;
  readonly state: FetchSseState;
  readonly lastEventAt: number | null;
  readonly lastByteAt: number | null;
  /**
   * Whether a data event has arrived since the stream last opened.
   * False from the moment response headers arrive until the first data
   * event on that same response. A flag set on the attempt rather than
   * `lastEventAt` compared with an open time: the two can fall in the
   * same millisecond, and a wall-clock step would make the comparison
   * say anything.
   */
  readonly dataSinceOpen: boolean;
}

export interface FetchSseHandle {
  readonly site: SiteId;
  state(): FetchSseState;
  isStale(staleThresholdMs: number): boolean;
  lastEventAt(): number | null;
  disconnect(): void;
}

interface FetchSseTransportOptions {
  /** Per-site inbound queue cap for `events` (non-coalesced streams). */
  queueCap?: number;
  /** Stale threshold in ms. Default 60_000 for events. */
  staleThresholdMs?: number;
  /** Bounded-queue drop policy. Default 'drop_oldest'. */
  dropPolicy?: 'drop_oldest' | 'drop_newest';
  /** Max reconnect attempts before giving up. Default Infinity. */
  maxReconnectAttempts?: number;
  /**
   * Read-idle watchdog: abort and reconnect (with the normal backoff)
   * when no bytes at all — not even a `: ping` — have arrived on an
   * open stream for this long. Default 15_000; `0` / `Infinity`
   * disables it (tests only).
   *
   * Silence is never healthy here: `fleet_state` is published at 1 Hz
   * on this same multiplexed stream and the upstream plugin pings, so
   * a quiet socket means the platform plane behind the webserver has
   * wedged (zenoh-router-platform "Closing transport" → the
   * webserver's GETs time out) while the TCP connection itself stays
   * up. Without the watchdog the SPA sat frozen for minutes with the
   * top-bar dot still saying "Live" until the far end finally reset.
   */
  readIdleTimeoutMs?: number;
}

const DEFAULT_OPTS: Required<FetchSseTransportOptions> = {
  queueCap: 1024,
  staleThresholdMs: 60_000,
  dropPolicy: 'drop_oldest',
  maxReconnectAttempts: Number.POSITIVE_INFINITY,
  readIdleTimeoutMs: 15_000,
};

/**
 * Drop-in replacement for the legacy `ZenohSseTransport`. One
 * connection per active site; the transport re-establishes on
 * network error, token expiry, epoch change, or sequence gap (the
 * per-category reducer signals `recover()`).
 */
export class FetchSseTransport {
  private readonly _opts: Required<FetchSseTransportOptions>;
  private readonly _listeners = new Set<FetchSseListener>();
  private readonly _stateListeners = new Set<FetchSseStateListener>();
  private readonly _handles = new Map<SiteId, FetchSseHandleImpl>();
  private readonly _siteListeners = new Map<
    string,
    Set<(env: V1LiveEvent) => void>
  >();

  constructor(opts: FetchSseTransportOptions = {}) {
    this._opts = { ...DEFAULT_OPTS, ...opts };
  }

  connect(site: SiteId): FetchSseHandle {
    const existing = this._handles.get(site);
    if (existing && !existing.isClosed()) {
      return existing;
    }
    const handle = new FetchSseHandleImpl(
      site,
      this._opts,
      (event) => this._listeners.forEach((l) => l(event)),
      () => this._handles.delete(site),
      (state) => this._stateListeners.forEach((l) => l(state, site)),
    );
    this._handles.set(site, handle);
    handle.start();
    return handle;
  }

  disconnect(site: SiteId): void {
    const handle = this._handles.get(site);
    if (handle) handle.disconnect();
  }

  disconnectAll(): void {
    for (const h of this._handles.values()) h.disconnect();
    this._handles.clear();
  }

  onEvent(listener: FetchSseListener): () => void {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  }

  /**
   * Subscribe to a specific keyexpr pattern for a single site.
   * Compatibility shim for the Phase-0 `ManagedLive.subscribe(...)`
   * contract; the underlying transport fans events out by pattern.
   * Returns a `{ unsubscribe(): void }` handle to match the legacy
   * SubscriptionHandle contract.
   *
   * The Phase-0 contract was `subscribe(pattern, handler)`. The v1
   * contract is `subscribe(site, pattern, handler)`. Both are
   * supported during the migration; the legacy form derives the
   * site from the active connection.
   */
  subscribe<T = unknown>(
    siteOrPattern: SiteId | string,
    patternOrHandler: string | ((env: LegacyEnvelope<T>) => void),
    maybeHandler?: (env: LegacyEnvelope<T>) => void,
  ): { unsubscribe(): void } {
    let site: SiteId | null = null;
    let pattern: string;
    let handler: (env: LegacyEnvelope<T>) => void;
    if (typeof patternOrHandler === 'function') {
      // Legacy 2-arg form: subscribe(pattern, handler).
      pattern = String(siteOrPattern);
      handler = patternOrHandler;
    } else {
      // 3-arg form: subscribe(site, pattern, handler).
      site = siteOrPattern as SiteId;
      pattern = patternOrHandler;
      handler = maybeHandler as (env: LegacyEnvelope<T>) => void;
    }

    let set = this._siteListeners.get(site ?? '__any__');
    if (!set) {
      set = new Set();
      this._siteListeners.set(site ?? '__any__', set);
    }
    set.add(handler as unknown as (env: V1LiveEvent) => void);
    const off = this.onEvent((evt) => {
      const evtSite = (evt as { envelope?: { site?: string } }).envelope?.site;
      if (site !== null && evtSite && evtSite !== site) return;
      // Translate v1 envelope → legacy LiveEventEnvelope shape so
      // the Phase-0 hooks (`useLiveData`, `useTaskLog`) keep
      // consuming `env.key` and `env.payload` until the per-screen
      // refactor lands.
      const legacy = v1ToLegacyEnvelope<T>(evt, pattern);
      handler(legacy);
    });
    return {
      unsubscribe: () => {
        off();
        set?.delete(handler as unknown as (env: V1LiveEvent) => void);
      },
    };
  }

  /**
   * Subscribe to transport-level state changes (open / connecting /
   * closed / stale / recovering). The handler gets the state and, as
   * an appended second argument, the site whose stream changed; a
   * one-argument handler is still a valid handler.
   */
  onState(handler: FetchSseStateListener): () => void {
    this._stateListeners.add(handler);
    return () => this._stateListeners.delete(handler);
  }

  /** This site's stream as it stands, or null when none is held. */
  siteSnapshot(site: SiteId): FetchSseSiteSnapshot | null {
    const h = this._handles.get(site);
    if (!h || h.isClosed()) return null;
    return {
      site,
      state: h.state(),
      lastEventAt: h.lastEventAt(),
      lastByteAt: h.lastByteAt(),
      dataSinceOpen: h.dataSinceOpen(),
    };
  }

  /**
   * The wake path's reconnect (plan W3 step 5): re-handshake this
   * site's stream unless something has arrived on it, or an attempt
   * has started, within `idleMs`. Returns whether it restarted.
   *
   * WHY THE READ-IDLE WATCHDOG IS NOT ENOUGH. A hidden tab's timers
   * are throttled, and a frozen or suspended page runs none at all,
   * so the watchdog that should have torn down a dead socket may
   * simply not have fired yet when the page comes back -- and a
   * reconnect backoff pending from before can still be tens of
   * seconds away. A healthy stream (bytes within `idleMs`) is left
   * alone: one stream per handle, always, and no needless re-handshake
   * on every glance at the tab.
   */
  restartIfIdle(site: SiteId, idleMs: number = this._wakeIdleMs()): boolean {
    const h = this._handles.get(site);
    if (!h || h.isClosed()) return false;
    return h.restartIfIdle(idleMs);
  }

  private _wakeIdleMs(): number {
    const ms = this._opts.readIdleTimeoutMs;
    return ms > 0 && Number.isFinite(ms) ? ms : DEFAULT_OPTS.readIdleTimeoutMs;
  }

  /**
   * Token rotation (INV-7, `ZitadelProvider`): re-handshake every
   * open stream so the fresh Bearer is what Envoy's ext_authz sees.
   *
   * This used to call `disconnect()` then `start()` on each handle.
   * `disconnect()` marks the handle closed (and drops it from the
   * registry), so the `start()` right after it was a no-op — at the
   * first JWT expiry the live stream died permanently and the map
   * froze until a reload. `restart()` swaps the connection without
   * closing the handle, so the same handle carries on.
   */
  rotateToken(): void {
    for (const h of this._handles.values()) h.restart();
  }

  state(): FetchSseState {
    for (const h of this._handles.values()) {
      if (h.state() === 'open') return 'open';
      if (h.state() === 'connecting') return 'connecting';
    }
    return this._handles.size === 0 ? 'idle' : 'closed';
  }
}

// ----------------------------------------------------------------------------

class FetchSseHandleImpl implements FetchSseHandle {
  readonly site: SiteId;
  private readonly _opts: Required<FetchSseTransportOptions>;
  private readonly _onEvent: (e: V1LiveEvent) => void;
  private readonly _onClosed: () => void;

  private _state: FetchSseState = 'idle';
  private _lastEventAt: number | null = null;
  /** `Date.now()` at the last chunk of ANY bytes (pings included). */
  private _lastByteAt: number | null = null;
  /** A data event has arrived on the response that is open now. */
  private _dataSinceOpen = false;
  /** `Date.now()` when the current or most recent attempt began. */
  private _attemptAt: number | null = null;
  private _lastEpoch: number | null = null;
  /**
   * The one in-flight connection attempt (fetch pending, or stream
   * being consumed), or null. Every path that ends an attempt early
   * (`disconnect`/`recover`/`restart`/the read-idle watchdog) nulls
   * this BEFORE aborting, which is how `_connect`'s `finally` tells
   * "my stream died on its own → I schedule the reconnect" apart from
   * "someone superseded me and already decided what happens next".
   */
  private _abortController: AbortController | null = null;
  /**
   * The single pending (re)start timer. It guards every scheduled
   * start — backoff reconnect, no-token retry, `recover()`'s resync
   * defer. There was no such guard before: a read error scheduled a
   * reconnect from BOTH `_consumeSseStream`'s `finally` and
   * `_connect`'s `catch`, so every error doubled the number of live
   * streams (envoy showed ~250 concurrent `/live/v1` streams after a
   * restart with two browsers open).
   */
  private _startTimer: ReturnType<typeof setTimeout> | null = null;
  private _reconnectAttempt = 0;
  private _closed = false;
  private readonly _onStateChange: (s: FetchSseState) => void;

  constructor(
    site: SiteId,
    opts: Required<FetchSseTransportOptions>,
    onEvent: (e: V1LiveEvent) => void,
    onClosed: () => void,
    onStateChange: (s: FetchSseState) => void,
  ) {
    this.site = site;
    this._opts = opts;
    this._onEvent = onEvent;
    this._onClosed = onClosed;
    this._onStateChange = onStateChange;
  }

  state(): FetchSseState {
    return this._state;
  }

  lastEventAt(): number | null {
    return this._lastEventAt;
  }

  lastByteAt(): number | null {
    return this._lastByteAt;
  }

  dataSinceOpen(): boolean {
    return this._dataSinceOpen;
  }

  isStale(staleThresholdMs: number): boolean {
    if (this._lastEventAt === null) return false;
    return Date.now() - this._lastEventAt > staleThresholdMs;
  }

  isClosed(): boolean {
    return this._closed;
  }

  start(): void {
    if (this._closed) return;
    // A start now supersedes any scheduled one, and never overlaps an
    // attempt already in flight: one stream per handle, always.
    this._clearStartTimer();
    if (this._abortController !== null) return;
    void this._connect();
  }

  disconnect(): void {
    this._closed = true;
    this._clearStartTimer();
    this._abortActive();
    this._setState('closed');
    this._onClosed();
  }

  /**
   * Re-handshake without closing the handle (token rotation). The
   * current attempt is aborted and a new one starts immediately with
   * whatever `getCurrentToken()` returns now. The backoff counter is
   * left alone — it resets on the next successful open — so a
   * rotation while the server is down costs one extra attempt, not a
   * fresh burst.
   */
  restart(): void {
    if (this._closed) return;
    this._abortActive();
    this.start();
  }

  /**
   * `restart()` unless the stream showed life within `idleMs`: a byte
   * arrived, or the attempt now IN FLIGHT began (a connect that started
   * a moment ago is not idle -- aborting it would only start the same
   * one again). A finished, failed attempt vouches for nothing, so a
   * pending backoff is skipped. An age that is negative or not a number
   * means the wall clock stepped; that cannot vouch for the stream
   * either, so it restarts.
   */
  restartIfIdle(idleMs: number): boolean {
    if (this._closed) return false;
    const inFlight = this._abortController !== null;
    const lastSign = Math.max(
      this._lastByteAt ?? -Infinity,
      inFlight ? (this._attemptAt ?? -Infinity) : -Infinity,
    );
    const age = Date.now() - lastSign;
    if (age >= 0 && age < idleMs) return false;
    this.restart();
    return true;
  }

  /**
   * Called by the per-category reducer when it observes an epoch
   * change or sequence gap. Triggers a snapshot resync by closing
   * the current connection and re-establishing (the SPA then
   * refetches the exact category snapshot before consuming any new
   * events).
   */
  recover(): void {
    this._abortActive();
    this._setState('recovering');
    // Defer reconnection slightly so the SPA reducer can refetch
    // the snapshot from the API first. Replaces (rather than adds
    // to) any backoff timer already pending.
    this._clearStartTimer();
    this._scheduleStart(100);
  }

  private _setState(s: FetchSseState): void {
    if (this._state !== s) {
      this._state = s;
      // Notify transport-level state listeners (e.g. useLiveStatus,
      // status banner) so the recovery window — where the
      // connection is dropped for snapshot resync — is observable.
      // The setter already guards on equality, so this fires only
      // on actual transitions. The transport passes a notifier
      // callback in the constructor so the handle does not have a
      // back-reference to the transport's internal listener set.
      this._onStateChange(s);
    }
  }

  /**
   * Abort the in-flight attempt, if any, in a way its own `finally`
   * recognises as "superseded" (see `_abortController`).
   */
  private _abortActive(): void {
    const ac = this._abortController;
    if (ac === null) return;
    this._abortController = null;
    ac.abort();
  }

  private _clearStartTimer(): void {
    if (this._startTimer !== null) {
      clearTimeout(this._startTimer);
      this._startTimer = null;
    }
  }

  /** Schedule exactly one future `start()`; a no-op while one is pending. */
  private _scheduleStart(delayMs: number): void {
    if (this._closed || this._startTimer !== null) return;
    this._startTimer = setTimeout(() => {
      this._startTimer = null;
      this.start();
    }, delayMs);
  }

  private async _connect(): Promise<void> {
    if (this._closed || this._abortController !== null) return;
    this._setState('connecting');

    const cfg = getRuntimeConfig();
    const token = getCurrentToken();
    if (!token) {
      // No token: defer until auth bootstraps.
      this._setState('idle');
      this._scheduleStart(1000);
      return;
    }

    const url = `${cfg.apiBaseUrl}/live/v1/sites/${encodeURIComponent(this.site)}/**`;
    const ac = new AbortController();
    this._abortController = ac;
    this._attemptAt = Date.now();

    try {
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'text/event-stream',
          Authorization: `Bearer ${token}`,
        },
        cache: 'no-store',
        signal: ac.signal,
      });

      if (!res.ok || res.body === null) {
        throw new Error(`fetch-SSE failed: HTTP ${res.status}`);
      }

      // Before the state change: its listeners read the snapshot, and
      // nothing has arrived on THIS response yet.
      this._dataSinceOpen = false;
      this._setState('open');
      this._reconnectAttempt = 0;

      await this._consumeSseStream(res.body, ac);
      // Falls through: the server closed the stream cleanly, which
      // is a disconnect like any other.
    } catch {
      // Network / HTTP / mid-stream read error, or our own abort —
      // all decided below, in exactly one place.
    } finally {
      if (this._abortController === ac) {
        // Ended on its own: nobody superseded this attempt, so it is
        // this attempt's job to schedule the (single) reconnect.
        this._abortController = null;
        this._scheduleReconnect();
      }
    }
  }

  private async _consumeSseStream(
    body: ReadableStream<Uint8Array>,
    ac: AbortController,
  ): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';

    // Read-idle watchdog (see `readIdleTimeoutMs`). Re-armed on every
    // chunk, pings included: the question is "is anything arriving at
    // all", not "is anything useful arriving".
    let idleTimer: ReturnType<typeof setTimeout> | null = null;
    const armWatchdog = () => {
      if (idleTimer !== null) clearTimeout(idleTimer);
      idleTimer = null;
      const ms = this._opts.readIdleTimeoutMs;
      if (!(ms > 0) || !Number.isFinite(ms)) return;
      idleTimer = setTimeout(() => {
        idleTimer = null;
        this._onReadIdle(ac);
      }, ms);
    };
    // Aborting the fetch signal tears down a real response body, but a
    // reader parked in `read()` on a stream that never yields (exactly
    // the stall case) is only released by cancelling it explicitly.
    const onAbort = () => {
      void reader.cancel().catch(() => undefined);
    };
    ac.signal.addEventListener('abort', onAbort);

    try {
      armWatchdog();
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        this._lastByteAt = Date.now();
        armWatchdog();
        buffer += decoder.decode(value, { stream: true });

        // SSE messages are separated by a blank line.
        let idx;
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
          const raw = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const msg = this._parseSseMessage(raw);
          if (msg) {
            if (msg.kind === 'ping') continue;
            this._handleData(msg.data);
            // `_handleData` may have `recover()`-ed (epoch change): the
            // rest of this chunk belongs to a stream we have abandoned.
            if (ac.signal.aborted) return;
          }
        }
      }
    } finally {
      if (idleTimer !== null) clearTimeout(idleTimer);
      ac.signal.removeEventListener('abort', onAbort);
      try {
        reader.releaseLock();
      } catch {
        // Already released by the cancel above.
      }
    }
  }

  /**
   * Watchdog trip: the stream is up but silent. Abort it and reconnect
   * with the normal backoff. The `'stale'` transition is what lets a
   * state listener (and a test) tell this apart from a plain error.
   */
  private _onReadIdle(ac: AbortController): void {
    if (this._closed || this._abortController !== ac) return;
    this._abortController = null;
    ac.abort();
    this._setState('stale');
    this._scheduleReconnect();
  }

  private _parseSseMessage(
    raw: string,
  ): { kind: 'ping' } | { kind: 'data'; data: string } | null {
    const lines = raw.split('\n');
    let data = '';
    for (const line of lines) {
      if (line.startsWith(':')) {
        // SSE comment (used for ping heartbeats by the upstream plugin).
        return { kind: 'ping' };
      }
      if (line.startsWith('data:')) {
        data += line.slice(5).trim();
      }
      // Other SSE fields (event:, id:, retry:) are ignored for v1.
    }
    if (!data) return null;
    return { kind: 'data', data };
  }
  private _handleData(raw: string): void {
    // `deriveV1LiveEvent` already rejects samples with a missing or
    // unrecognised `schema_version` (plan: "The SPA refuses to
    // render a payload whose schema_version it does not
    // understand."), so a valid `event` here always has a valid
    // envelope.
    const event = deriveV1LiveEvent(raw);
    if (!event) return;

    this._lastEventAt = Date.now();
    this._dataSinceOpen = true;

    // Epoch-change detection (plan §Live consistency protocol).
    //
    // A per-category `category_sequence` "gap" check used to live
    // here too (comparing each event's sequence against `last + 1`,
    // `recover()`-ing — aborting and re-establishing the WHOLE
    // multiplexed connection, all categories at once — on any
    // mismatch). Removed: `category_sequence` is one counter SHARED
    // across every entity in a category (confirmed in
    // `worker/projection.py::V1Projector.next_envelope`, keyed by
    // category alone, not per-entity), and a fresh subscription's
    // initial replay of retained state does not arrive in sequence
    // order — confirmed empirically, a live `GET .../site-alpha/**`
    // returned doors as `seq=32307, 32298, 32290, ...` and tasks as
    // `seq=247, 149, 256, 189, ...`, fully out of order. With 16+
    // doors and 15+ tasks sharing one counter each, that guaranteed a
    // spurious "gap" on almost every connect, which tore down the
    // entire stream (robots included) and reconnected into ANOTHER
    // randomly-ordered burst — the confirmed cause of "robot position
    // on the map doesn't update without a manual refresh": the
    // connection was thrashing, never staying up long enough to
    // accumulate live position updates. The worker also does not
    // publish `manifest` events at all (confirmed: no such call
    // exists anywhere in the worker package), so the "resync via
    // manifest" half of this protocol was never implemented either —
    // there was nothing correct for `recover()` to resync against.
    // The per-item `mergeById` merge in `useLiveData.ts` already
    // tolerates out-of-order/duplicate delivery fine (last-write-wins
    // per entity id), so no replacement ordering check is needed here.
    if (event.envelope) {
      if (this._lastEpoch !== null && event.envelope.projector_epoch !== this._lastEpoch) {
        // New epoch (e.g. the worker restarted): every category's
        // sequence counter starts over from a fresh baseline on the
        // new projector instance. `recover()` used to be called here
        // with `_lastEpoch` left completely untouched -- so the very
        // first event on the RECONNECTED stream (still carrying this
        // same new epoch) immediately re-triggered this identical
        // branch again, forever: an infinite abort/reconnect loop
        // that silently dropped every live event from that point on
        // and, since each loop iteration opens a fresh HTTP request,
        // was aggressive enough to visibly starve unrelated requests
        // (confirmed cause of both "Last Update never advances" and
        // "lift session takes 20+ seconds to acquire" -- any worker
        // restart while a browser tab was open triggered this).
        // Adopting the new epoch BEFORE recovering breaks the loop:
        // the next event correctly matches `_lastEpoch` and is
        // processed normally.
        this._lastEpoch = event.envelope.projector_epoch;
        this.recover();
        return;
      }
      this._lastEpoch = event.envelope.projector_epoch;
    }

    this._onEvent(event);
  }

  private _scheduleReconnect(): void {
    if (this._closed || this._startTimer !== null) return;
    if (this._reconnectAttempt >= this._opts.maxReconnectAttempts) {
      this._setState('closed');
      return;
    }
    const delay = nextReconnectDelay(this._reconnectAttempt);
    this._reconnectAttempt += 1;
    this._setState('closed');
    this._scheduleStart(delay);
  }
}

// ----------------------------------------------------------------------------
// Convenience singleton
// ----------------------------------------------------------------------------

let _singleton: FetchSseTransport | null = null;
export function fetchSseTransport(): FetchSseTransport {
  if (!_singleton) _singleton = new FetchSseTransport();
  return _singleton;
}

// ----------------------------------------------------------------------------
// Legacy LiveEventEnvelope shape + v1 → legacy adapter
// (Phase-0 → v1 migration shim).
//
// The legacy `useLiveData` hook reads `env.key` and `env.payload`
// directly off each event. The v1 envelope puts the payload at the
// top level and identifies the entity by category + envelope
// metadata. We synthesise a `key` from the category to keep the
// legacy hook compiling during the migration.
// ----------------------------------------------------------------------------

export interface LegacyEnvelope<T = unknown> {
  key: string;
  payload: T;
  category?: string;
  site?: string;
  timestamp?: number;
  epoch?: number;
}

function v1ToLegacyEnvelope<T>(evt: V1LiveEvent, pattern: string): LegacyEnvelope<T> {
  const payload =
    evt.kind === 'snapshot' || evt.kind === 'event'
      ? (evt.payload as unknown as T)
      : evt.kind === 'manifest' || evt.kind === 'tombstone'
        ? (evt.payload as unknown as T)
        : (evt.payload as unknown as T);
  const category =
    evt.kind === 'snapshot' || evt.kind === 'event' ? evt.category : evt.kind;
  return {
    key: pattern,
    payload,
    category,
    site: evt.envelope?.site,
    timestamp: evt.envelope?.projector_received_at_ms,
    epoch: evt.envelope?.projector_epoch,
  };
}